import {
  useEffect,
  useState
} from 'react';

// ALLOW_PDF_IMAGES_V1

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function headers(
  extra = {}
) {
  return {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY,

    ...extra
  };
}


async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
      ' failed (' +
      response.status +
      '): ' +
      (
        text ||
        response.statusText
      )
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


function newRow(
  allowPdf,
  allowDocx,
  index
) {
  return {
    id:
      null,

    localKey:
      'new-' +
      Date.now() +
      '-' +
      index,

    label:
      '',

    required:
      true,

    allow_pdf:
      allowPdf,

    allow_docx:
      allowDocx
  };
}


export default function SubmissionFileSettingsEditor({
  courseId,
  assignmentTypeId,
  initialSettings,
  onSaved
}) {
  const [
    allowPdf,
    setAllowPdf
  ] =
    useState(
      initialSettings
        ?.allowed_pdf !==
      false
    );

  const [
    allowDocx,
    setAllowDocx
  ] =
    useState(
      initialSettings
        ?.allowed_docx !==
      false
    );

  const [
    multiFile,
    setMultiFile
  ] =
    useState(
      initialSettings
        ?.multi_file_submission ===
      true
    );

  const [
    allowPdfImages,
    setAllowPdfImages
  ] =
    useState(
      initialSettings
        ?.allow_pdf_images ===
      true
    );

  const [
    rows,
    setRows
  ] =
    useState([]);

  const [
    deletedIds,
    setDeletedIds
  ] =
    useState([]);

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    saving,
    setSaving
  ] =
    useState(false);

  const [
    message,
    setMessage
  ] =
    useState('');


  useEffect(() => {
    setAllowPdf(
      initialSettings
        ?.allowed_pdf !==
      false
    );

    setAllowDocx(
      initialSettings
        ?.allowed_docx !==
      false
    );

    setMultiFile(
      initialSettings
        ?.multi_file_submission ===
      true
    );

    setAllowPdfImages(
      initialSettings
        ?.allow_pdf_images ===
      true
    );
  }, [
    initialSettings
      ?.allowed_pdf,
    initialSettings
      ?.allowed_docx,
    initialSettings
      ?.multi_file_submission,
    initialSettings
      ?.allow_pdf_images
  ]);


  useEffect(() => {
    let cancelled =
      false;

    async function load() {
      setLoading(true);

      try {
        const response =
          await fetch(
            SUPABASE_URL +
              '/rest/v1/course_assignment_submission_file_slots' +
              '?course_id=eq.' +
              encodeURIComponent(
                courseId
              ) +
              '&assignment_type_id=eq.' +
              encodeURIComponent(
                assignmentTypeId
              ) +
              '&select=*' +
              '&order=display_order.asc,created_at.asc',
            {
              headers:
                headers()
            }
          );

        const data =
          await readJson(
            response,
            'Loading submission file settings'
          );

        if (!cancelled) {
          setRows(
            Array.isArray(data)
              ? data
              : []
          );

          setDeletedIds([]);
        }

      } catch (error) {
        console.error(
          'Loading submission file settings failed:',
          error
        );

        if (!cancelled) {
          setMessage(
            error?.message ||
            'Could not load submission file settings.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled =
        true;
    };
  }, [
    courseId,
    assignmentTypeId
  ]);


  function updateRow(
    index,
    patch
  ) {
    setRows(
      previous =>
        previous.map(
          (
            row,
            rowIndex
          ) =>
            rowIndex ===
              index
              ? {
                  ...row,
                  ...patch
                }
              : row
        )
    );
  }


  function addRow() {
    setRows(
      previous => [
        ...previous,

        newRow(
          allowPdf,
          allowDocx,
          previous.length
        )
      ]
    );
  }


  function removeRow(
    index
  ) {
    const row =
      rows[index];

    if (row?.id) {
      setDeletedIds(
        previous => [
          ...previous,
          row.id
        ]
      );
    }

    setRows(
      previous =>
        previous.filter(
          (
            unused,
            rowIndex
          ) =>
            rowIndex !==
            index
        )
    );
  }


  function toggleMulti(
    checked
  ) {
    setMultiFile(
      checked
    );

    if (
      checked &&
      rows.length === 0
    ) {
      setRows([
        newRow(
          allowPdf,
          allowDocx,
          0
        )
      ]);
    }
  }


  async function save() {
    setMessage('');

    if (
      !allowPdf &&
      !allowDocx
    ) {
      setMessage(
        'Select at least one allowed file format.'
      );

      return;
    }

    if (multiFile) {
      if (!rows.length) {
        setMessage(
          'Add at least one submission file.'
        );

        return;
      }

      const invalidLabel =
        rows.find(
          row =>
            !String(
              row.label ||
              ''
            ).trim()
        );

      if (invalidLabel) {
        setMessage(
          'Every submission file needs a name.'
        );

        return;
      }

      const invalidFormat =
        rows.find(
          row =>
            !row.allow_pdf &&
            !row.allow_docx
        );

      if (invalidFormat) {
        setMessage(
          'Every submission file must allow at least one file format.'
        );

        return;
      }
    }

    setSaving(true);

    try {
      const settingResponse =
        await fetch(
          SUPABASE_URL +
            '/rest/v1/course_assignment_type_settings' +
            '?on_conflict=course_id,assignment_type_id',
          {
            method:
              'POST',

            headers:
              headers({
                'Content-Type':
                  'application/json',

                Prefer:
                  'resolution=merge-duplicates,return=representation'
              }),

            body:
              JSON.stringify({
                course_id:
                  courseId,

                assignment_type_id:
                  assignmentTypeId,

                allowed_pdf:
                  allowPdf,

                allowed_docx:
                  allowDocx,

                multi_file_submission:
                  multiFile,

                allow_pdf_images:
                  allowPdfImages,

                updated_at:
                  new Date()
                    .toISOString()
              })
          }
        );

      await readJson(
        settingResponse,
        'Saving file settings'
      );

      for (
        const id
        of deletedIds
      ) {
        const response =
          await fetch(
            SUPABASE_URL +
              '/rest/v1/course_assignment_submission_file_slots' +
              '?id=eq.' +
              encodeURIComponent(
                id
              ),
            {
              method:
                'DELETE',

              headers:
                headers()
            }
          );

        if (!response.ok) {
          throw new Error(
            'Removing submission file failed (' +
            response.status +
            ').'
          );
        }
      }

      const savedRows = [];

      for (
        let index = 0;
        index < rows.length;
        index += 1
      ) {
        const row =
          rows[index];

        const payload = {
          course_id:
            courseId,

          assignment_type_id:
            assignmentTypeId,

          label:
            String(
              row.label ||
              ''
            ).trim(),

          required:
            row.required !==
            false,

          allow_pdf:
            row.allow_pdf ===
            true,

          allow_docx:
            row.allow_docx ===
            true,

          display_order:
            index,

          updated_at:
            new Date()
              .toISOString()
        };

        const response =
          await fetch(
            row.id
              ? (
                  SUPABASE_URL +
                  '/rest/v1/course_assignment_submission_file_slots' +
                  '?id=eq.' +
                  encodeURIComponent(
                    row.id
                  )
                )
              : (
                  SUPABASE_URL +
                  '/rest/v1/course_assignment_submission_file_slots'
                ),
            {
              method:
                row.id
                  ? 'PATCH'
                  : 'POST',

              headers:
                headers({
                  'Content-Type':
                    'application/json',

                  Prefer:
                    'return=representation'
                }),

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const saved =
          await readJson(
            response,
            'Saving submission file'
          );

        if (
          Array.isArray(saved) &&
          saved[0]
        ) {
          savedRows.push(
            saved[0]
          );
        }
      }

      setRows(
        savedRows
      );

      setDeletedIds([]);

      if (onSaved) {
        onSaved({
          allowed_pdf:
            allowPdf,

          allowed_docx:
            allowDocx,

          multi_file_submission:
            multiFile,

          allow_pdf_images:
            allowPdfImages
        });
      }

      setMessage(
        'Submission file settings saved.'
      );

    } catch (error) {
      console.error(
        'Saving submission file settings failed:',
        error
      );

      setMessage(
        error?.message ||
        'Could not save submission file settings.'
      );

    } finally {
      setSaving(false);
    }
  }


  return (
    <div
      style={
        styles.wrap
      }
    >
      <div
        style={
          styles.heading
        }
      >
        Submission Files
      </div>

      <div
        style={
          styles.help
        }
      >
        Choose which document formats students may submit.
        PDF and Word are allowed by default.
      </div>

      <div
        style={
          styles.formatRow
        }
      >
        <strong>
          Allowed file formats
        </strong>

        <label
          style={
            styles.checkLabel
          }
        >
          <input
            type="checkbox"
            checked={
              allowPdf
            }
            onChange={
              event =>
                setAllowPdf(
                  event.target.checked
                )
            }
          />

          PDF
        </label>

        <label
          style={
            styles.checkLabel
          }
        >
          <input
            type="checkbox"
            checked={
              allowDocx
            }
            onChange={
              event =>
                setAllowDocx(
                  event.target.checked
                )
            }
          />

          Word (.docx)
        </label>
      </div>

      <label
        style={
          styles.multiToggle
        }
      >
        <input
          type="checkbox"
          checked={
            multiFile
          }
          onChange={
            event =>
              toggleMulti(
                event.target.checked
              )
          }
        />

        <strong>
          Multi-file submission
        </strong>
      </label>

      <label
        style={
          styles.multiToggle
        }
        title="Allow image-only or scanned PDFs that do not contain selectable text."
      >
        <input
          type="checkbox"
          checked={
            allowPdfImages
          }
          onChange={
            event =>
              setAllowPdfImages(
                event.target.checked
              )
          }
        />

        <strong>
          Allow PDF images
        </strong>
      </label>

      {multiFile && (
        <div
          style={
            styles.multiArea
          }
        >
          <div
            style={
              styles.multiHelp
            }
          >
            Each row is one submission file.
            The PDF and DOCX boxes on that row determine
            what the student may upload for that file.
          </div>

          {loading ? (
            <div
              style={
                styles.help
              }
            >
              Loading...
            </div>

          ) : (
            rows.map(
              (
                row,
                index
              ) => (
                <div
                  key={
                    row.id ||
                    row.localKey
                  }
                  style={
                    styles.slotRow
                  }
                >
                  <input
                    type="text"
                    style={
                      styles.nameInput
                    }
                    value={
                      row.label ||
                      ''
                    }
                    placeholder="e.g. Tracked changes version"
                    onChange={
                      event =>
                        updateRow(
                          index,
                          {
                            label:
                              event
                                .target
                                .value
                          }
                        )
                    }
                  />

                  <label
                    style={
                      styles.checkLabel
                    }
                  >
                    <input
                      type="checkbox"
                      checked={
                        row.required !==
                        false
                      }
                      onChange={
                        event =>
                          updateRow(
                            index,
                            {
                              required:
                                event
                                  .target
                                  .checked
                            }
                          )
                      }
                    />

                    Required
                  </label>

                  <label
                    style={
                      styles.checkLabel
                    }
                  >
                    <input
                      type="checkbox"
                      checked={
                        row.allow_pdf ===
                        true
                      }
                      onChange={
                        event =>
                          updateRow(
                            index,
                            {
                              allow_pdf:
                                event
                                  .target
                                  .checked
                            }
                          )
                      }
                    />

                    PDF
                  </label>

                  <label
                    style={
                      styles.checkLabel
                    }
                  >
                    <input
                      type="checkbox"
                      checked={
                        row.allow_docx ===
                        true
                      }
                      onChange={
                        event =>
                          updateRow(
                            index,
                            {
                              allow_docx:
                                event
                                  .target
                                  .checked
                            }
                          )
                      }
                    />

                    DOCX
                  </label>

                  <button
                    type="button"
                    style={
                      styles.removeBtn
                    }
                    onClick={
                      () =>
                        removeRow(
                          index
                        )
                    }
                  >
                    ×
                  </button>
                </div>
              )
            )
          )}

          <button
            type="button"
            style={
              styles.addBtn
            }
            onClick={
              addRow
            }
          >
            + Add submission file
          </button>
        </div>
      )}

      <button
        type="button"
        style={
          styles.saveBtn
        }
        disabled={
          saving ||
          loading
        }
        onClick={
          save
        }
      >
        {saving
          ? 'Saving...'
          : 'Save Submission File Settings'}
      </button>

      {message && (
        <div
          style={{
            ...styles.message,

            color:
              message ===
              'Submission file settings saved.'
                ? '#276738'
                : '#a61b1b'
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}


const styles = {
  wrap: {
    marginTop:
      '18px',
    paddingTop:
      '18px',
    borderTop:
      '1px solid #e6ebef'
  },

  heading: {
    color:
      '#334e68',
    fontWeight:
      700,
    marginBottom:
      '5px'
  },

  help: {
    color:
      '#829ab1',
    fontSize:
      '12px',
    lineHeight:
      1.45,
    marginBottom:
      '10px'
  },

  formatRow: {
    display:
      'flex',
    gap:
      '18px',
    alignItems:
      'center',
    flexWrap:
      'wrap',
    marginBottom:
      '13px',
    fontSize:
      '13px'
  },

  checkLabel: {
    display:
      'flex',
    gap:
      '6px',
    alignItems:
      'center',
    fontSize:
      '13px'
  },

  multiToggle: {
    display:
      'inline-flex',
    gap:
      '7px',
    alignItems:
      'center',
    fontSize:
      '13px',
    marginRight:
      '18px',
    marginBottom:
      '10px'
  },

  multiArea: {
    border:
      '1px solid #d7dee5',
    borderRadius:
      '8px',
    padding:
      '12px',
    background:
      '#f8fafc',
    marginBottom:
      '12px'
  },

  multiHelp: {
    color:
      '#66788a',
    fontSize:
      '12px',
    lineHeight:
      1.45,
    marginBottom:
      '10px'
  },

  slotRow: {
    display:
      'grid',
    gridTemplateColumns:
      'minmax(190px, 1fr) auto auto auto auto',
    gap:
      '10px',
    alignItems:
      'center',
    marginBottom:
      '8px'
  },

  nameInput: {
    width:
      '100%',
    minWidth:
      0,
    boxSizing:
      'border-box',
    padding:
      '8px 9px',
    border:
      '1px solid #bcccdc',
    borderRadius:
      '6px',
    font:
      'inherit'
  },

  removeBtn: {
    width:
      '28px',
    height:
      '28px',
    border:
      '1px solid #e0b7b7',
    borderRadius:
      '6px',
    background:
      'white',
    color:
      '#a12a2a',
    cursor:
      'pointer',
    fontSize:
      '18px',
    lineHeight:
      1
  },

  addBtn: {
    border:
      '1px solid #9fb5c4',
    borderRadius:
      '6px',
    background:
      'white',
    color:
      '#215b7d',
    padding:
      '6px 10px',
    fontWeight:
      700,
    cursor:
      'pointer',
    fontSize:
      '12px'
  },

  saveBtn: {
    marginTop:
      '4px',
    border:
      'none',
    borderRadius:
      '6px',
    background:
      '#215b7d',
    color:
      'white',
    padding:
      '8px 12px',
    fontWeight:
      700,
    cursor:
      'pointer'
  },

  message: {
    marginTop:
      '8px',
    fontSize:
      '13px'
  }
};
